import { Component, OnInit, inject } from "@angular/core";
import { FindingReportDataService } from "@app/findingreport/services";
import { FindingReportWorkspaceModel } from "@app/findingreport/shared/models";
import { Pages } from "@constants/pages/Pages";
import { FilterType } from "@enums/FilterType";
import { FormatType } from "@enums/FormatType";
import { ModuleType } from "@enums/ModuleType";
import { RendererType } from "@enums/RendererType";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { WorkspaceModulePermissionModel } from "@models/permissions/WorkspaceModulePermissions";
import { FilterConfig } from "@models/workspace/FilterConfig";
import { RendererConfig } from "@models/workspace/RendererConfig";
import { PermissionsService } from "@services/permissions/PermissionsService";
import { TabService } from "@services/tabs/TabService";
import { firstValueFrom } from "rxjs";
import { injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/findingreport/i18n/scope";
import { WorkspaceComponent } from "@app/shared/workspace/workspace.component";

@Component({
    selector: "finding-report-overview",
    templateUrl: "./finding-report-overview.component.html",
    styleUrls: ["./finding-report-overview.component.scss"],
    imports: [WorkspaceComponent],
    viewProviders: [provideTranslationScope(scope)],
})
export class FindingReportOverviewComponent implements OnInit {
    private findingReportDataService = inject(FindingReportDataService);
    private tabService = inject(TabService);
    private _translation = injectTranslation(scope);
    pages = inject(Pages);
    private permissionsService = inject(PermissionsService);

    moduleType = ModuleType.FR;
    tabID: string;
    tab: TabModel;

    data: Promise<any[]>;
    headerLookup: any = {};

    filterConfigs: FilterConfig[] = [];

    rendererConfig: RendererConfig[] = [
        {
            textColumn: "overallScore",
            actionColumn: "overallScoreColor",
            hideActionColumn: true,
            type: RendererType.Score,
        },
        {
            textColumn: "reportDate",
            type: RendererType.Default,
            formatType: FormatType.DateFormat,
        },
        {
            textColumn: "reportDueDate",
            type: RendererType.Default,
            formatType: FormatType.DateFormat,
        },
    ];

    numberProps: string[] = [
        "numberOfMois",
        "numberOfOpenMois",
        "numberOfAssessments",
    ];
    dateProps: string[] = ["reportDate", "reportDueDate"];

    permissions: WorkspaceModulePermissionModel;

    async ngOnInit() {
        this.permissions = this.permissionsService.permissions.Workspace.findingReport;
        await this._translation.ready();
        const t = (key: string) => this._translation.t(key);
        this.headerLookup = {
            identifier: t("identifierHeader"),
            reportType: t("reportTypeHeader"),
            reportNumber: t("reportNumberHeader"),
            reportName: t("reportNameHeader"),
            subName: t("subNameHeader"),
            overallScore: t("overallScoreHeader"),
            ownerOrganization: t("ownerOrganizationHeader"),
            reportDate: t("reportDateHeader"),
            reportDueDate: t("reportDueDateHeader"),
            ownerName: t("ownerNameHeader"),
            reviewerOrganization: t("reviewerOrganizationHeader"),
            reviewerName: t("reviewerNameHeader"),
            validationType: t("validationTypeHeader"),
            numberOfMois: t("numberOfMoisHeader"),
            numberOfOpenMois: t("numberOfOpenMoisHeader"),
            numberOfAssessments: t("numberOfAssessmentsHeader"),
            organizationPath: t("organizationPathHeader"),
        };
        this.filterConfigs = [
            {
                label: t("organizationsResponsibleFilterLabel"),
                column: "organizationId",
                type: FilterType.Organization,
                hideActionColumn: true,
                param: "",
            },
        ];
        this.loadData();
    }

    private loadData() {
        this.data = firstValueFrom(this.findingReportDataService
            .getFindingReports())
            .then(this.mapHeaders);
    }

    openDetail(fr: FindingReportWorkspaceModel) {
        this.tabService.generateTab(
            this.pages.ImDetail,
            fr.guid,
            this._translation.t("openDetailTabTitle", {
                identifier: +fr.identifier,
                reportName: fr.reportName,
            })
        );
    }

    add() {
        this.tabService.generateTab(
            this.pages.ImDetail,
            null,
            this._translation.t("addNewTabTitle")
        );
    }

    mapHeaders = (rows) => {
        if (rows && rows.length) {
            Object.getOwnPropertyNames(rows[0]).forEach((propName) => {
                if (!this.headerLookup[propName]) {
                    this.headerLookup[propName] = propName;
                }
            });
        }
        return rows;
    };
}
